import api from "lib/axios";
import { authStorage } from "services/authStorage";

type ApiSuccess<T> = { status: "success" | string; result: { data: T }; message: string };
type ApiError = { status: "error" | string; message: string; result: null };

export type InstructorAuthUser = {
  id: number;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  image?: string | null;
  image_url?: string | null;
  is_active?: boolean | number | string;
  status_label?: "active" | "inactive" | string;
  education_type?: "basic" | null;
  token?: string;
  abilities?: string[];
  roles?: string[];
  permissions?: string[];
  stages?: Array<{ id: number; name: string; slug?: string }>;
  grades?: Array<{ id: number; name: string; stage_id?: number }>;
  subjects?: Array<{
    id: number;
    name: string;
    education_type?: "basic" | null;
    stage_id?: number | null;
    stage_ids?: number[];
    grade_ids?: number[];
    grade_id?: number | null;
  }>;
};

type SendOtpBody = { phone: string };
type VerifyOtpBody = { phone: string; code: string };
type PasswordBody = { phone: string; password: string };
type RegisterBody = PasswordBody & {
  name: string;
  email?: string | null;
  password_confirmation: string;
  stage_ids: number[];
  grade_ids: number[];
  subject_ids: number[];
};

export const instructorAuthService = {
  async register(body: RegisterBody): Promise<InstructorAuthUser> {
    const response = await api.post<ApiSuccess<InstructorAuthUser>>("/instructors/register", body);
    return response.data.result?.data;
  },

  async sendOtp(body: SendOtpBody): Promise<{ message: string }> {
    const response = await api.post<ApiSuccess<unknown> | ApiError>("/instructors/otp/send", body);
    return { message: (response.data as any)?.message ?? "OTP sent." };
  },

  async verifyOtp(body: VerifyOtpBody): Promise<InstructorAuthUser> {
    const response = await api.post<ApiSuccess<InstructorAuthUser>>("/instructors/otp/verify", body);
    const user = response.data.result?.data;
    if (user?.token) authStorage.setToken("instructor", user.token);
    return user;
  },

  async login(body: PasswordBody): Promise<InstructorAuthUser> {
    const response = await api.post<ApiSuccess<InstructorAuthUser>>("/instructors/login", body);
    const user = response.data.result?.data;
    if (user?.token) authStorage.setToken("instructor", user.token);
    return user;
  },

  async sendPasswordResetOtp(body: SendOtpBody): Promise<{ message: string }> {
    const response = await api.post<ApiSuccess<unknown> | ApiError>("/instructors/password/forgot", body);
    return { message: (response.data as any)?.message ?? "Password reset code sent." };
  },

  async verifyPasswordResetOtp(body: VerifyOtpBody): Promise<{ reset_token: string; expires_in: number }> {
    const response = await api.post<ApiSuccess<{ reset_token: string; expires_in: number }>>(
      "/instructors/password/verify-otp",
      body
    );
    return response.data.result?.data;
  },

  async resetPassword(body: PasswordBody & { reset_token: string; password_confirmation: string }): Promise<void> {
    await api.post("/instructors/password/reset", body);
  },

  async getProfile() {
    const response = await api.get<ApiSuccess<InstructorAuthUser>>("/instructors/profile", {
      meta: { toastSuccess: false },
    });
    return response.data.result?.data;
  },

  async updateProfile(data: Record<string, unknown>) {
    const response = await api.put<ApiSuccess<InstructorAuthUser>>("/instructors/profile", data);
    return response.data.result?.data;
  },

  async updatePassword(data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }) {
    const response = await api.put("/instructors/password", data);
    return response.data;
  },

  logout() {
    authStorage.clearToken("instructor");
    window.location.href = authStorage.loginPath("instructor");
  },
};
